import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import html2pdf from 'html2pdf.js';
import QuoteComments from './QuoteComments';
import { toast } from 'react-hot-toast';
import { trackInteraction, startTimeTracking, stopTimeTracking, trackSectionViews } from '../utils/interactionTracking';
import { useAnalytics } from '../hooks/useAnalytics';

const Watermark = ({ businessName }) => (
  <div className="flex items-center justify-center py-3 mb-4 bg-gray-50 rounded-lg" style={{ borderBottom: `1px solid ${THEME.primary}20` }}>
    <span className="text-sm" style={{ color: THEME.subtext }}>Quote created through </span>
    <img src="/logo.png" alt="Aided Logo" className="h-6 w-auto mr-2" />
  </div>
);

export default function PublicQuote() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [sectionObserver, setSectionObserver] = useState(null);
  const { logEvent } = useAnalytics();

  useEffect(() => {
    fetchQuote();
    checkUser();

    if(id){
      // Track page view
      trackInteraction(id, 'view', 'quote');
      startTimeTracking(id, 'quote');
      
      // Setup section tracking after a short delay to ensure DOM is ready
      setTimeout(() => {
        const observer = trackSectionViews(id, 'quote');
        setSectionObserver(observer);
      }, 1000);
    }

    return () => {
      stopTimeTracking();
      if (sectionObserver) {
        sectionObserver.disconnect();
      }
    };
  }, [id]);

  useEffect(() => {
    if (id) {
      logEvent('PublicQuote', 'View Public Quote', `Quote ID: ${id}`);
    }
  }, [id]);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    } catch (error) {
      console.error('Error checking user:', error);
    }
  };

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_public_quote', {
        quote_unique_id: id
      });

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Quote not found');

      const quoteData = data[0];
      console.log(quoteData);
      setQuote({
        id,
        quotes: {
          ...quoteData.quote_data,
          items: quoteData.quote_items,
          clients: quoteData.client_data,
          business_profile: quoteData.business_data,
          user_id: quoteData.quote_data.user_id
        },
        customization_options: quoteData.customization_options
      });
    } catch (error) {
      console.error('Error fetching quote:', error);
      setError('Quote not found or no longer available');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      logEvent('PublicQuote', 'Download PDF', `Quote ID: ${id}`);
      // Track download event
      await trackInteraction(id, 'pdf_download', 'quote');
      
      const element = document.getElementById('quote-content');
      const opt = {
        margin: 1,
        filename: `quote-${quote?.quotes?.quote_number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(element).save();
      logEvent('PublicQuote', 'Download PDF Success', `Quote ID: ${id}`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      logEvent('PublicQuote', 'Download PDF Error', error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'accepted': return THEME.success;
      case 'declined': return THEME.error;
      case 'sent': return THEME.warning;
      default: return THEME.primary;
    }
  };

  const handleQuoteAction = async (action) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.rpc(
        action === 'accept' ? 'accept_quote' : 'decline_quote',
        { quote_unique_id: id, status_unique_by: !user ? 'client' : 'business'}
      );
      
      if (error) throw error;

      // Track action
      await trackInteraction(id, action, 'quote');

      // Show success message
      toast.success(
        action === 'accept' 
          ? 'Quote accepted successfully!' 
          : 'Quote declined successfully!'
      );

      // Refresh the page to show updated status
      window.location.reload();

      if (action === 'accept') {
        logEvent('PublicQuote', 'Accept Quote', `Quote ID: ${id}`);
        logEvent('PublicQuote', 'Accept Quote Success', `Quote ID: ${id}`);
      } else {
        logEvent('PublicQuote', 'Reject Quote', `Quote ID: ${id}`);
        logEvent('PublicQuote', 'Reject Quote Success', `Quote ID: ${id}`);
      }
    } catch (err) {
      console.error(`Error ${action}ing quote:`, err);
      setError(`Failed to ${action} quote. Please try again.`);
      toast.error(`Failed to ${action} quote. Please try again.`);

      if (action === 'accept') {
        logEvent('PublicQuote', 'Accept Quote Error', err.message);
      } else {
        logEvent('PublicQuote', 'Reject Quote Error', err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: THEME.primary }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.background} transparent` }} />
      </div>
    );
  }

  if (error || !quote?.quotes) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: THEME.primary }}>
        <div className="bg-white rounded-xl p-8 text-center max-w-md mx-auto" style={{ boxShadow: shadows.lg }}>
          <h1 className="text-2xl font-bold mb-2" style={{ color: THEME.error }}>
            Quote Not Found
          </h1>
          <p style={{ color: THEME.subtext }}>
            The quote you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const { quotes: quoteData, customization_options } = quote;
  const { business_profile: business } = quoteData;
  const showPrices = customization_options?.showPrices ?? true;
  const showLineItems = customization_options?.showLineItems ?? true;
  const allowComments = customization_options?.allowComments ?? true;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Download Button */}
        <div className="mt-6 flex justify-end" data-section-name="download-button">
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200"
            style={{ 
              background: THEME.background,
              boxShadow: shadows.md,
              color: THEME.primary,
            }}
          >
            <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
            Download Quote
          </button>
        </div>

        {/* Status Badge - show when quote is already Accepted or Declined */}
        {(quoteData.status === 'Accepted' || quoteData.status === 'Declined') && (
          <div className="mt-8 flex justify-center">
            <div
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                quoteData.status === 'Accepted'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              Quote {quoteData.status}
            </div>
          </div>
        )}

        {/* Main Quote Content */}
        <div id="quote-content" className="space-y-6">
          {/* Header Section */}
          <div className="bg-white rounded-xl p-6" data-section-name="header" style={{ boxShadow: shadows.lg }}>
            <Watermark businessName={business?.business_name} />
            <div className="flex justify-between items-start">
              {/* Logo and Business Info */}
              <div className="flex-1">
                {business?.logo_url && (
                  <img
                    src={business.logo_url}
                    alt="Business Logo"
                    className="h-16 w-auto object-contain mb-4"
                  />
                )}
                <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>
                  {business?.business_name}
                </h1>
                {business?.business_address && (
                  <p className="mt-1" style={{ color: THEME.subtext }}>
                    {business.business_address}
                  </p>
                )}
                {business?.business_phone && (
                  <p className="mt-1" style={{ color: THEME.subtext }}>
                    {business.business_phone}
                  </p>
                )}
                {business?.business_email && (
                  <p className="mt-1" style={{ color: THEME.subtext }}>
                    {business.business_email}
                  </p>
                )}
              </div>

              {/* Quote Details */}
              <div className="text-right">
                <h2 className="text-2xl font-bold mb-2" style={{ color: THEME.text }}>
                  Quote #{quoteData.quote_number}
                </h2>
                <p className="text-sm" style={{ color: THEME.subtext }}>
                  Date: {new Date(quoteData.quote_date).toLocaleDateString()}
                </p>
                <p className="text-sm" style={{ color: THEME.subtext }}>
                  Valid Until: {new Date(quoteData.expiry_date).toLocaleDateString()}
                </p>
                <div className="mt-2">
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${getStatusColor(quoteData.status)}15`,
                      color: getStatusColor(quoteData.status),
                    }}
                  >
                    {quoteData.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Client Information
              </h3>
              <p style={{ color: THEME.text }}>{quoteData.clients?.client_name}</p>
              <p style={{ color: THEME.subtext }}>{quoteData.clients?.client_email}</p>
            </div>
          </div>

          {/* Quote Items */}
          {showLineItems && (
            <div className="bg-white rounded-xl p-6" data-section-name="quote-items" style={{ boxShadow: shadows.lg }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
                Quote Items
              </h3>
              <div className="space-y-4">
                {quoteData.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: THEME.text }}>
                        {item.description}
                      </p>
                      {showPrices && (
                        <p className="text-sm" style={{ color: THEME.subtext }}>
                          {item.quantity} x ${item.unit_price}
                        </p>
                      )}
                    </div>
                    {showPrices && (
                      <p className="font-medium" style={{ color: THEME.text }}>
                        ${(item.quantity * item.unit_price).toFixed(2)}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {showPrices && (
                <div className="mt-6 border-t border-gray-100 pt-4">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between">
                        <span style={{ color: THEME.subtext }}>Subtotal</span>
                        <span style={{ color: THEME.text }}>${quoteData.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: THEME.subtext }}>Tax ({quoteData.tax_rate}%)</span>
                        <span style={{ color: THEME.text }}>${quoteData.tax_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-semibold">
                        <span style={{ color: THEME.text }}>Total</span>
                        <span style={{ color: THEME.primary }}>${quoteData.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes and Terms */}
          {(quoteData.notes || quoteData.terms) && (
            <div className="bg-white rounded-xl p-6" data-section-name="notes-terms" style={{ boxShadow: shadows.lg }}>
              {quoteData.notes && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: THEME.text }}>
                    Notes
                  </h3>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: THEME.subtext }}>
                    {quoteData.notes}
                  </p>
                </div>
              )}
              {quoteData.terms && (
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: THEME.text }}>
                    Terms & Conditions
                  </h3>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: THEME.subtext }}>
                    {quoteData.terms}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>


        {/* Action Buttons - show only if quote status is not already Accepted or Declined */}
        {quoteData.status === 'Sent' && !user && (
          <div className="mt-8 space-y-4" data-section-name="actions">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => handleQuoteAction('accept')}
                disabled={loading}
                className={`px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  loading 
                    ? 'bg-primary/50 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Processing...
                  </div>
                ) : (
                  'Accept Quote'
                )}
              </button>
              <button
                onClick={() => handleQuoteAction('decline')}
                disabled={loading}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {loading ? 'Processing...' : 'Decline Quote'}
              </button>
            </div>

            {error && (
              <div className="text-center text-red-600 bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Comments Section */}
        {allowComments && (
          <div className="mt-8" data-section-name="comments">
            <h2 className="text-xl font-semibold mb-4" style={{ color: THEME.text }}>
              Comments
            </h2>
            <QuoteComments 
              quoteId={quoteData.quote_id} 
              user={user}
              publicQuoteId={id}
              isPublicView={true}
              quoteOwnerId={quoteData.user_id}
              businessName={business?.business_name}
            />
          </div>
        )}
      </div>
    </div>
  );
} 